import "../ContentManagement.css";
import React, { useState } from "react";
import { MdAddTask } from "react-icons/md";
import { VscTasklist } from "react-icons/vsc";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import x from "../../Components/Assets/Management/x-task.png";
import fb from "../../Components/Assets/Management/fb-task.png";
import li from "../../Components/Assets/Management/linkedin-task.png";
import insta from "../../Components/Assets/Management/instagram-task.png";
import mail from "../../Components/Assets/Management/email-icn.png";
import utube from "../../Components/Assets/Management/youtube.png";

// Helper function to get date with specified days added
const getDateWithDaysAdded = (baseDate, days) => {
  const date = new Date(baseDate);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

// Helper function to get tomorrow's date
const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split("T")[0];
};

/**
 * Add Task component - creates new tasks with validation and recurring options
 */
export function AddTask({
  companyProfile, // Company data for task creation
  onTaskCreated, // Callback when task is created
  onClose, // Callback to close the form
}) {
  const navigate = useNavigate();

  // Task form state
  const [taskForm, setTaskForm] = useState({
    companyProfileId: "",
    title: "",
    tagline: "",
    taskpurpose: "",
    headline: "",
    offerormessage: "",
    calltoaction: "",
    guidlines: "",
    additionalinfo: "",
    description: "",
    targetPostingDate: "",
    submissionForReview: "",
    currentStatus: "Pending",
    platform_x: false,
    platform_fb: false,
    platform_insta: false,
    platform_li: false,
    platform_youtube: false, // Add YouTube platform
    platform_via_mail: false,
    isRecurring: false,
    recurrenceType: "", // "weekly", "bi-weekly", "monthly"
    recurrenceStartDate: "",
    recurrenceEndDate: "",
    weeklyDayOfWeek: "monday", // Weekly/bi-weekly day
    monthlyWeek: "first", // Monthly week selection
    monthlyDayOfWeek: "monday", // Monthly day selection
  });

  const [submitStatus, setSubmitStatus] = useState("idle"); // Submit state tracking
  const [monthLimitWarning, setMonthLimitWarning] = useState(""); // Month limit warning message
  const [submissionInputError, setSubmissionInputError] = useState(false); // Track submission input error state

  // Fetch all tasks for month validation
  const fetchAllTasks = async () => {
    try {
      if (!companyProfile?._id) {
        console.log("Company profile not loaded yet");
        return [];
      }

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/task/tasks/${companyProfile._id}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data; // Return all tasks without filtering
    } catch (error) {
      console.error("Error fetching all tasks:", error);
      return [];
    }
  };

  // Form input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTaskForm((prevState) => {
      const newState = {
        ...prevState,
        [name]: value,
      };
      
      // If submission date is changed and target date exists, validate target date
      if (name === "submissionForReview" && prevState.targetPostingDate) {
        const submissionDate = new Date(value);
        const targetDate = new Date(prevState.targetPostingDate);
        
        // Calculate minimum target date (submission date + 3 days)
        const minTargetDate = new Date(submissionDate);
        minTargetDate.setDate(minTargetDate.getDate() + 3);
        
        // If target date is before minimum allowed date, clear target date
        if (targetDate < minTargetDate) {
          newState.targetPostingDate = "";
        }
      }
      
      // Clear submission input error when user selects a date
      if (name === "submissionForReview" && value) {
        setSubmissionInputError(false);
      }
      
      return newState;
    });
  };

  // Handle date change with month limit validation
  const handleTargetDateChange = async (e) => {
    const { name, value } = e.target;
    
    // Check if submission date is selected before allowing target date selection
    if (name === "targetPostingDate" && !taskForm.submissionForReview) {
      setSubmissionInputError(true);
      toast.error("Please select submission date first", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      return;
    }
    
    // Validate 3-day gap
    if (name === "targetPostingDate" && value && taskForm.submissionForReview) {
      const submissionDate = new Date(taskForm.submissionForReview);
      const targetDate = new Date(value);
      const daysDifference = Math.ceil((targetDate - submissionDate) / (1000 * 60 * 60 * 24));
      
      if (daysDifference < 3) {
        toast.error("Target posting date must be at least 3 days after submission date", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        return;
      }
    }

    setTaskForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Real-time validation for single tasks
    if (name === "targetPostingDate" && value && !taskForm.isRecurring) {
      const targetDate = new Date(value);
      const targetMonth = targetDate.getMonth();
      const targetYear = targetDate.getFullYear();

      try {
        const allTasks = await fetchAllTasks();
        const tasksInTargetMonth = allTasks
          .filter((task) => {
            const taskDate = new Date(task.targetPostingDate);
            return (
              taskDate.getMonth() === targetMonth &&
              taskDate.getFullYear() === targetYear
            );
          }).length;

        if (tasksInTargetMonth >= 4) {
          setMonthLimitWarning(
            `Warning: ${tasksInTargetMonth} tasks already exist for ${targetDate.toLocaleDateString(
              "en-US",
              { month: "long", year: "numeric" }
            )}. Maximum 4 tasks allowed per month.`
          );
        } else {
          setMonthLimitWarning("");
        }
      } catch (error) {
        console.error("Error checking month limit:", error);
      }
    } else {
      setMonthLimitWarning("");
    }
  };

  const handleRecurrenceCheckbox = (e) => {
    setTaskForm((prev) => ({
      ...prev,
      isRecurring: e.target.checked,
      // Reset fields based on recurrence state
      ...(e.target.checked
        ? {
            targetPostingDate: "",
            submissionForReview: "",
          }
        : {
            recurrenceType: "",
            recurrenceStartDate: "",
            recurrenceEndDate: "",
            monthlyWeek: "first",
            monthlyDayOfWeek: "monday",
          }),
    }));

    setMonthLimitWarning("");
  };

  // Handle recurrence type changes
  const handleRecurrenceChange = (e) => {
    const { name, value } = e.target;
    setTaskForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle monthly recurrence options
  const handleMonthlyRecurrenceChange = (e) => {
    const { name, value, type } = e.target;
    setTaskForm((prev) => ({
      ...prev,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  // Handle weekly recurrence options
  const handleWeeklyRecurrenceChange = (e) => {
    const { name, value } = e.target;
    setTaskForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handler for checkbox changes:
  const handlePlatformCheckbox = (e) => {
    const { name, checked } = e.target;
    console.log(`Platform checkbox changed: ${name} = ${checked}`);
    setTaskForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Enhance the form submission with more detailed debugging
  const taskHandleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus("submitting");

    // Proactively notify other components to refresh task lists on submit click
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      try { window.dispatchEvent(new CustomEvent('hussme:tasks-updated')); } catch (_) { try { window.dispatchEvent(new Event('hussme:tasks-updated')); } catch (_) {} }
    }

    // More detailed debugging for form values
    console.log("Task form submission - ALL form values:", taskForm);
    console.log("Form submission - platform_youtube value:", taskForm.platform_youtube);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("You must be logged in to create a task", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        navigate("/login");
        return;
      }

      // Validate required fields based on recurrence
      if (!taskForm.title) {
        toast.error("Please fill in the task title", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        setSubmitStatus("idle");
        return;
      }

      // If recurring, validate recurrence fields instead of dates
      if (taskForm.isRecurring) {
        if (
          !taskForm.recurrenceType ||
          !taskForm.recurrenceStartDate ||
          !taskForm.recurrenceEndDate
        ) {
          toast.error("Please fill in all recurrence fields", {
            className: "gradient-toast",
            bodyClassName: "gradient-toast-body",
            progressClassName: "gradient-toast-progress",
          });
          setSubmitStatus("idle");
          return;
        }
      } else {
        // If not recurring, validate date fields
        if (!taskForm.targetPostingDate || !taskForm.submissionForReview) {
          toast.error("Please fill in all required date fields", {
            className: "gradient-toast",
            bodyClassName: "gradient-toast-body",
            progressClassName: "gradient-toast-progress",
          });
          setSubmitStatus("idle");
          return;
        }

        // Check 4-task-per-month limit for single tasks
        const targetDate = new Date(taskForm.targetPostingDate);
        const targetMonth = targetDate.getMonth();
        const targetYear = targetDate.getFullYear();

        // Fetch all tasks (including all statuses) for accurate count
        const allTasks = await fetchAllTasks();
        const tasksInTargetMonth = allTasks
          .filter((task) => {
            const taskDate = new Date(task.targetPostingDate);
            return (
              taskDate.getMonth() === targetMonth &&
              taskDate.getFullYear() === targetYear
            );
          }).length;

        if (tasksInTargetMonth >= 4) {
          toast.error(
            `Cannot create task. Maximum 4 tasks allowed per month. ${tasksInTargetMonth} tasks already exist for ${targetDate.toLocaleDateString(
              "en-US",
              { month: "long", year: "numeric" }
            )}.`,
            {
              className: "gradient-toast",
              bodyClassName: "gradient-toast-body",
              progressClassName: "gradient-toast-progress",
              autoClose: 5000,
            }
          );
          setSubmitStatus("idle");
          return;
        }
      }

      // Prepare form data
      const formData = new FormData();
      formData.append("companyProfileId", companyProfile._id);
      formData.append("title", taskForm.title);
      formData.append("tagline", taskForm.tagline);
      formData.append("taskpurpose", taskForm.taskpurpose);
      formData.append("headline", taskForm.headline);
      formData.append("offerormessage", taskForm.offerormessage);
      formData.append("calltoaction", taskForm.calltoaction);
      formData.append("guidlines", taskForm.guidlines);
      formData.append("additionalinfo", taskForm.additionalinfo);
      formData.append("description", taskForm.description);

      // Only append dates if not recurring or if they are provided
      if (!taskForm.isRecurring) {
        formData.append("targetPostingDate", taskForm.targetPostingDate);
        formData.append("submissionForReview", taskForm.submissionForReview);
      } else {
        // For recurring tasks, dates are optional
        if (taskForm.targetPostingDate) {
          formData.append("targetPostingDate", taskForm.targetPostingDate);
        }
        if (taskForm.submissionForReview) {
          formData.append("submissionForReview", taskForm.submissionForReview);
        }
      }

      // Add referenceImage to the form data if a file is selected
      const referenceImageInput = document.querySelector(
        'input[name="referenceimage"]'
      );
      if (referenceImageInput && referenceImageInput.files[0]) {
        formData.append("referenceImage", referenceImageInput.files[0]);
      }

      // Add platform fields with explicit logging
      console.log("Setting platform values in FormData:");
      
      // Platform X
      formData.append("platform_x", taskForm.platform_x ? "true" : "false");
      console.log("- platform_x:", taskForm.platform_x ? "true" : "false");
      
      // Platform FB
      formData.append("platform_fb", taskForm.platform_fb ? "true" : "false");
      console.log("- platform_fb:", taskForm.platform_fb ? "true" : "false");
      
      // Platform Instagram
      formData.append("platform_insta", taskForm.platform_insta ? "true" : "false");
      console.log("- platform_insta:", taskForm.platform_insta ? "true" : "false");
      
      // Platform LinkedIn
      formData.append("platform_li", taskForm.platform_li ? "true" : "false");
      console.log("- platform_li:", taskForm.platform_li ? "true" : "false");
      
      // Platform YouTube - Focus on this one
      console.log("- platform_youtube setting to:", taskForm.platform_youtube ? "true" : "false");
      formData.append("platform_youtube", taskForm.platform_youtube ? "true" : "false");
      
      // Platform Email
      formData.append("platform_via_mail", taskForm.platform_via_mail ? "true" : "false");
      console.log("- platform_via_mail:", taskForm.platform_via_mail ? "true" : "false");

      // Add recurrence fields
      formData.append("isRecurring", taskForm.isRecurring ? "true" : "false");
      formData.append("recurrenceType", taskForm.recurrenceType || "");
      formData.append(
        "recurrenceStartDate",
        taskForm.recurrenceStartDate || ""
      );
      formData.append("recurrenceEndDate", taskForm.recurrenceEndDate || "");

      // Add weekly/bi-weekly recurrence fields
      if (
        taskForm.recurrenceType === "weekly" ||
        taskForm.recurrenceType === "bi-weekly"
      ) {
        formData.append("weeklyDayOfWeek", taskForm.weeklyDayOfWeek);
      }

      // Add monthly recurrence fields
      if (taskForm.recurrenceType === "monthly") {
        formData.append("monthlyWeek", taskForm.monthlyWeek);
        formData.append("monthlyDayOfWeek", taskForm.monthlyDayOfWeek);
      }

      // Send the request to the server
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/task/create-task`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      // Log the response data to verify what was saved
      console.log("Task creation response:", responseData);
      console.log("Platform values in response:", {
        x: responseData.platform_x,
        fb: responseData.platform_fb,
        insta: responseData.platform_insta,
        li: responseData.platform_li,
        youtube: responseData.platform_youtube,
        mail: responseData.platform_via_mail
      });
      
      if (responseData) {
        toast.success("Task created successfully!", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        setSubmitStatus("submitted");

        // Notify other components to refresh their task lists
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          try { window.dispatchEvent(new CustomEvent('hussme:tasks-updated')); } catch (_) { try { window.dispatchEvent(new Event('hussme:tasks-updated')); } catch (_) {} }
        }

        // Reset form
        setTaskForm({
          companyProfileId: "",
          title: "",
          tagline: "",
          taskpurpose: "",
          headline: "",
          offerormessage: "",
          calltoaction: "",
          guidlines: "",
          additionalinfo: "",
          description: "",
          targetPostingDate: "",
          submissionForReview: "",
          isRecurring: false,
          recurrenceType: "",
          recurrenceStartDate: "",
          recurrenceEndDate: "",
          weeklyDayOfWeek: "monday",
          monthlyWeek: "first",
          monthlyDayOfWeek: "monday",
          platform_x: false,
          platform_fb: false,
          platform_insta: false,
          platform_li: false,
          platform_youtube: false, // Add YouTube
          platform_via_mail: false,
        });

        // Call parent callbacks
        if (onTaskCreated) {
          await onTaskCreated(); // Refresh tasks in parent
        }
        if (onClose) {
          onClose(); // Close the form
        }

        setTimeout(() => setSubmitStatus("idle"), 1500);
      }
    } catch (error) {
      console.error("Error creating task:", error);

      // Check if it's a 4-task limit error
      if (
        error.response?.data?.message?.includes(
          "Maximum 4 tasks allowed per month"
        )
      ) {
        toast.error(error.response.data.message, {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
          autoClose: 6000,
        });
      } else {
        toast.error("Error creating task", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
      }
      setSubmitStatus("idle");
    }
  };
  return (
    <div className="assign-new-task">
      <div className="assign-new-task-row">
        <div className="new-task-form-title">
          Add New Task <MdAddTask style={{ marginLeft: "8px" }} />
        </div>
        <img
          onClick={() => onClose && onClose()}
          width="48"
          height="48"
          src="https://img.icons8.com/fluency/48/delete-sign.png"
          alt="delete-sign"
          className="status-cross-symbol"
        />
      </div>

      <form className="new-task-form" onSubmit={taskHandleSubmit}>
        <div className="new-task-row">
          <div className="new-task-row-input">
            <label htmlFor="title">
              Task Title: <span className="red-imp">*</span>
            </label>
            <input
              type="text"
              name="title"
              className="new-task-title"
              placeholder="e.g., Summer Campaign"
              value={taskForm.title}
              onChange={handleInputChange}
            />
          </div>
          <div className="new-task-row-input">
            <label htmlFor="taskpurpose">Task Objective:</label>
            <input
              type="text"
              name="taskpurpose"
              className="new-task-title"
              placeholder="e.g., Launch new product"
              value={taskForm.taskpurpose}
              onChange={handleInputChange}
            />
          </div>
          <div className="new-task-row-input">
            <label htmlFor="headline">Main Headline:</label>
            <input
              type="text"
              name="headline"
              className="new-task-title"
              placeholder="e.g., Meet the New Smartwatch"
              value={taskForm.headline}
              onChange={handleInputChange}
            />
          </div>
          <div className="new-task-row-input">
            <label htmlFor="calltoaction">Tagline for Lead:</label>
            <input
              type="text"
              name="calltoaction"
              className="new-task-title"
              placeholder="e.g., Shop Now | 50% Offer"
              value={taskForm.calltoaction}
              onChange={handleInputChange}
            />
          </div>
          <div className="new-task-row-input">
            <label htmlFor="description">Description:</label>
            <textarea
              name="description"
              className="new-task-des"
              placeholder="e.g., Fun tone, eco-friendly"
              value={taskForm.description}
              onChange={handleInputChange}
            ></textarea>
          </div>
          <div className="new-task-row-input">
            <label htmlFor="referenceimage">Reference Flyer or Product:</label>
            <input
              type="file"
              name="referenceimage"
              className="img-input"
              accept="image/*"
            />
          </div>
          <div className="platform-selector-container">
            <label className="platform-selector-label">Select Platforms:</label>

            <div className="platform-checkbox-wrapper">
              <label className="platform-checkbox-item">
                <input
                  type="checkbox"
                  name="platform_x"
                  checked={taskForm.platform_x}
                  onChange={handlePlatformCheckbox}
                />
                <span className="platform-checkmark"></span>
                <img src={x} className="platform-icon" alt="X" />
              </label>
              <label className="platform-checkbox-item">
                <input
                  type="checkbox"
                  name="platform_fb"
                  checked={taskForm.platform_fb}
                  onChange={handlePlatformCheckbox}
                />
                <span className="platform-checkmark"></span>
                <img
                  src={fb}
                  className="platform-icon"
                  alt="Facebook"
                />
              </label>
              <label className="platform-checkbox-item">
                <input
                  type="checkbox"
                  name="platform_insta"
                  checked={taskForm.platform_insta}
                  onChange={handlePlatformCheckbox}
                />
                <span className="platform-checkmark"></span>
                <img
                  src={insta}
                  className="platform-icon"
                  alt="Instagram"
                />
              </label>
              <label className="platform-checkbox-item">
                <input
                  type="checkbox"
                  name="platform_li"
                  checked={taskForm.platform_li}
                  onChange={handlePlatformCheckbox}
                />
                <span className="platform-checkmark"></span>
                <img
                  src={li}
                  className="platform-icon"
                  alt="LinkedIn"
                />
              </label>
              <label className="platform-checkbox-item">
                <input
                  type="checkbox"
                  name="platform_youtube"
                  checked={taskForm.platform_youtube}
                  onChange={handlePlatformCheckbox}
                />
                <span className="platform-checkmark"></span>
                <img
                  src={utube}
                  className="platform-icon"
                  alt="YouTube"
                />
              </label>
              <label className="platform-checkbox-item">
                <input
                  type="checkbox"
                  name="platform_via_mail"
                  checked={taskForm.platform_via_mail}
                  onChange={handlePlatformCheckbox}
                />
                <span className="platform-checkmark"></span>
                <img
                  src={mail}
                  className="platform-icon platform-icon-mail"
                  alt="Mail"
                />
              </label>
            </div>
          </div>
          <div className="recurring-task-checkbox-container">
            <label className="recurring-task-checkbox-item">
              <input
                type="checkbox"
                name="platform_fb"
                checked={taskForm.isRecurring}
                onChange={handleRecurrenceCheckbox}
              />
              <span className="recurring-task-checkmark"></span>
              <h2 className="recurring-task-label-text"> Repeat this task</h2>
            </label>
          </div>
          {taskForm.isRecurring && (
            <div className="for-recurring-date-row add">
              <div className="new-task-input-box">
                <label
                  htmlFor="recurrenceStartDate"
                  className="new-task-date-label"
                >
                  Start Date:<span className="red-imp">*</span>
                </label>
                <input
                  type="date"
                  name="recurrenceStartDate"
                  className="new-task-date recurring-date"
                  value={taskForm.recurrenceStartDate}
                  onChange={handleRecurrenceChange}
                  min={getTomorrowDate()}
                  required={taskForm.isRecurring}
                />
              </div>{" "}
              <div className="new-task-input-box recurring-dropdown-gap">
                <label htmlFor="recurrenceType" className="new-task-date-label">
                  Repeat:
                </label>
                <select
                  name="recurrenceType"
                  className="new-task-date recurring-dropdown-gap"
                  value={taskForm.recurrenceType}
                  onChange={handleRecurrenceChange}
                  required={taskForm.isRecurring}
                >
                  <option value="">Select</option>
                  <option value="weekly">Weekly</option>
                  <option value="bi-weekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div className="new-task-input-box">
                <label
                  htmlFor="recurrenceEndDate"
                  className="new-task-date-label"
                >
                  Repeat Until:<span className="red-imp">*</span>
                </label>
                <input
                  type="date"
                  name="recurrenceEndDate"
                  className="new-task-date recurring-date"
                  value={taskForm.recurrenceEndDate}
                  onChange={handleRecurrenceChange}
                  min={taskForm.recurrenceStartDate || getTomorrowDate()}
                  required={taskForm.isRecurring}
                />
              </div>
            </div>
          )}

          {taskForm.isRecurring &&
            (taskForm.recurrenceType === "weekly" ||
              taskForm.recurrenceType === "bi-weekly") && (
              <div className="for-date-row add">
                <div className="new-task-input-box">
                  <label className="new-task-date-label">Schedule on:</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <select
                      name="weeklyDayOfWeek"
                      className="new-task-date "
                      value={taskForm.weeklyDayOfWeek}
                      onChange={handleWeeklyRecurrenceChange}
                      style={{ width: "120px" }}
                    >
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                    <span>
                      of every{" "}
                      {taskForm.recurrenceType === "weekly"
                        ? "week"
                        : "two weeks"}
                    </span>
                  </div>
                </div>
                <div className="new-task-input-box">
                  {/* Empty div for spacing */}
                </div>
              </div>
            )}
          {taskForm.isRecurring && taskForm.recurrenceType === "monthly" && (
            <div className="for-date-row add">
              <div className="new-task-input-box">
                <label className="new-task-date-label">Schedule on:</label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <select
                    name="monthlyWeek"
                    className="new-task-date"
                    value={taskForm.monthlyWeek}
                    onChange={handleMonthlyRecurrenceChange}
                    style={{ width: "100px" }}
                  >
                    <option value="first">1st</option>
                    <option value="second">2nd</option>
                    <option value="third">3rd</option>
                    <option value="fourth">4th</option>
                    <option value="last">Last</option>
                  </select>
                  <select
                    name="monthlyDayOfWeek"
                    className="new-task-date"
                    value={taskForm.monthlyDayOfWeek}
                    onChange={handleMonthlyRecurrenceChange}
                    style={{ width: "120px" }}
                  >
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                  <span>of every month</span>
                </div>
              </div>
              <div className="new-task-input-box">
                {/* Empty div for spacing */}
              </div>
            </div>
          )}
          {!taskForm.isRecurring && (
            <div className="for-date-row add">
              <div className="new-task-input-box">
                <label
                  htmlFor="submissionForReview"
                  className="new-task-date-label"
                >
                  Submission for Review:<span className="red-imp">*</span>
                </label>
                <input
                  type="date"
                  name="submissionForReview"
                  className={`new-task-date ${submissionInputError ? 'input-error' : ''}`}
                  value={taskForm.submissionForReview}
                  onChange={handleInputChange}
                  min={getTomorrowDate()}
                />
              </div>

              <div className="new-task-input-box">
                <label
                  htmlFor="targetPostingDate"
                  className="new-task-date-label target-date-label"
                >
                  Target Posting Date:<span className="red-imp">*</span>
                </label>
                <input
                  type="date"
                  name="targetPostingDate"
                  className="new-task-date"
                  value={taskForm.targetPostingDate}
                  onChange={handleTargetDateChange}
                  min={taskForm.submissionForReview ? getDateWithDaysAdded(taskForm.submissionForReview, 3) : getTomorrowDate()}
                  disabled={!taskForm.submissionForReview}
                />
              </div>
            </div>
          )}
          {monthLimitWarning && (
            <div className="month-limit-warning">{monthLimitWarning}</div>
          )}
        </div>

        <div className="new-task-row-submit">
          <button
            type="submit"
            className="new-task-row-submit-btn"
            disabled={
              submitStatus === "submitting" || submitStatus === "submitted"
            }
          >
            {submitStatus === "submitting"
              ? "Submitting..."
              : submitStatus === "submitted"
              ? "Submitted"
              : "Submit Task"}
            <VscTasklist
              style={{
                marginLeft: "8px",
                height: "30px",
                width: "30px",
              }}
            />
          </button>
        </div>
      </form>
    </div>
  );
}
